import { Typography, Uploader } from '@/ui-kit';
import styles from './upload-photo-registartion-form.module.scss';
import { Photo, Profile } from '@/types/profile';
import { usePhotoUpload } from '@/hooks/use-photo-upload';

import { ButtonSubmitStep } from '../button-submit-step';
import React, { useEffect, useMemo, useState } from 'react';
import { useDeletePhotoById } from '@/hooks/use-delete-photo-by-id';
import { useAuth } from '@/store/useAuth';
import { eventsApi } from '@/api';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { usePhotoUpdate } from '@/hooks/use-photo-update';
import { useUploadCache } from '@/store/useUploadCache';
import WebApp from '@twa-dev/sdk';
import { ShieldIcon } from '@/assets/icons';
import { useVibration } from '@/store/useVibration';

interface Props {
    userData?: Profile | null;
}
export const UploadPhotoRegistartionForm: React.FC<Props> = ({ userData }) => {
    useEffect(() => {
        callYaMetrika('new_photo_screen');
        eventsApi.updateUserEvent('new_photo_screen');
        eventsApi.updateUserEvent('registration_step_view_new_photo_screen');
    }, []);

    const { mutateAsync: uploadPhoto } = usePhotoUpload();
    const { mutateAsync: deletePhotoById } = useDeletePhotoById();
    const { mutateAsync: updatePhoto } = usePhotoUpdate();
    const setUserData = useAuth((state) => state.setProfile);
    const { tempUrl, setTempUrl } = useUploadCache(state => state)

    const [progress, setProgress] = useState(0);

    const isPhotoUploading = !!progress;

    const photoCount = useMemo(() => {
        return userData?.photos?.length ?? 0;
    }, [userData?.photos?.length]);

    const [isEditLoading, setIsEditLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const { isVibrationEnabled } =
        useVibration(state => state);

    const handleUploadPhoto = async (file: File | null) => {
        if (!file) return;

        try {
            const formData = new FormData();
            formData.append('photo[image]', file);
            const { data } = await uploadPhoto({ dataPhoto: formData, callback: setProgress });
            if (userData?.photos?.length === 0) {
                callYaMetrika('1st_photo_loaded');
            }
            eventsApi.updateUserEvent('registration_photos');
            setTempUrl(null)
            setUserData({ ...userData, photos: [...(userData?.photos ?? []), data] });
            
            if (isVibrationEnabled) {
                WebApp
                    .HapticFeedback
                    .impactOccurred('medium');
                // pass for not android
                navigator.vibrate?.(10)
            }
        } finally {
            setProgress(0);
        }
    };

    const handleUpdatePhoto = async (photoId: number, file: File | null) => {
        if (!file || isEditLoading) return;

        setIsEditLoading(true)
        try {
            const formData = new FormData();
            formData.append('photo[image]', file);
            const { data } = await updatePhoto({ id: photoId, dataPhoto: formData, callback: setProgress });

            const userPhotos = userData?.photos ?? []
            const photos = [
                ...userPhotos
            ]
            photos[
                userPhotos
                    .findIndex(
                        (e: Photo) =>
                            e.id === photoId
                    )
            ] = data

            setUserData({ ...userData, photos });
            
            if (isVibrationEnabled) {
                WebApp
                    .HapticFeedback
                    .impactOccurred('medium');
                // pass for not android
                navigator.vibrate?.(10)
            }
        } finally {
            setProgress(0);
            setIsEditLoading(false)
        }
    };

    const handleDeletePhoto = async (photoId?: number) => {
        if (!photoId || photoCount === 1 || isDeleteLoading) return;
        setIsDeleteLoading(true);
        try {
            const res = await deletePhotoById(photoId);
            if (res.status === 200) {
                setUserData({ ...userData, photos: userData?.photos?.filter((photo) => photo.id !== photoId) });
            }
        } catch (error) {
            console.error(`Error in ${handleDeletePhoto.name}:`, error);
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return (
        <>
            <div className={styles.form}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center
                    >
                        Добавь фото
                    </Typography>
                    <div className={styles.formLabel}>
                        <Typography
                            center
                            variant="p"
                            fontWeight="medium"
                            typeColor="gray-label">
                            Ты в любой момент сможешь их заменить
                            <br />
                            или удалить. Фото должны быть реальными.
                        </Typography>
                    </div>
                    <div className={styles.uploaderBlok}>
                        {userData?.photos.map((photo, index) => (
                            <Uploader
                                key={photo.id}
                                photoUrl={photo.thumb_url}
                                isCanDelete={
                                    index > 0 ||
                                    userData.photos.length >= 2
                                }
                                isActive={index === 0}
                                isUploading={isPhotoUploading && index === 0 && isEditLoading}
                                progress={progress}
                                isDeleteLoading={isDeleteLoading}
                                isEditLoading={isEditLoading}
                                photoCount={photoCount}
                                onDeletePhoto={() => handleDeletePhoto(userData?.photos[index]?.id)}
                                onChange={(e) => handleUpdatePhoto(userData?.photos[index]?.id, e)} />
                        ))}

                        {Array.from({ length: 6 - (userData?.photos.length ?? 0) }).map((_, index) => {
                            const isActive = !index;

                            return (
                                <Uploader
                                    key={Math.random()}
                                    isActive={isActive}
                                    isUploading={isPhotoUploading && isActive && !isEditLoading}
                                    progress={progress}
                                    onChange={handleUploadPhoto} />
                            );
                        })}
                    </div>
                    <div className={styles.note}>
                        <div className={styles.noteIcon}>
                            <ShieldIcon />
                        </div>
                        <div className={styles.noteAside}>
                            <Typography
                                variant="p"
                                fontWeight="medium"
                                typeColor="gray-label"
                                className={styles.noteAsideTitle}>
                                Не переживай за свои фото
                            </Typography>
                            <Typography
                                variant="p"
                                fontWeight="medium"
                                typeColor="gray-label"
                                className={styles.noteAsideSubtitle}>
                                Они видны только участникам Euphora и не используются больше нигде.
                            </Typography>
                        </div>
                    </div>
                </div>
                <ButtonSubmitStep
                    stepNumber={3}
                    isDisabled={!photoCount}
                    href="/registrationV2/step/4"
                    isGray
                    onClick={() => eventsApi.updateUserEvent('registration_step_complete_new_photo_screen')} />
            </div>
        </>
    );
};
